import { createSlice } from '@reduxjs/toolkit';
import {
  acceptPaymentRefundThunk,
  createPaymentRefundThunk,
  fetchPaymentRefundsThunk,
  rejectPaymentRefundThunk
} from './paymentRefundsThunks';
import { type IPaymentRefundRequest } from '@/types/IPaymentRefundRequest';
import { type IErrorData } from '@/utils/getErrorData';

interface IPaymentRefundsState {
  items: IPaymentRefundRequest[];
  total: number | null;
  isLoading: boolean;
  isCreating: boolean;
  /** Флаги «идёт решение по заявке», ключ — UUID заявки. */
  resolvingByUuid: Record<string, boolean>;
  error: IErrorData | null;
}

const initialState: IPaymentRefundsState = {
  items: [],
  total: null,
  isLoading: false,
  isCreating: false,
  resolvingByUuid: {},
  error: null
};

const paymentRefundsSlice = createSlice({
  name: 'paymentRefunds',
  initialState,
  reducers: {
    resetPaymentRefundsState(state) {
      state.items = [];
      state.total = null;
      state.isLoading = false;
      state.isCreating = false;
      state.resolvingByUuid = {};
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentRefundsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentRefundsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchPaymentRefundsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? null;
      });

    builder
      .addCase(createPaymentRefundThunk.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createPaymentRefundThunk.fulfilled, (state) => {
        state.isCreating = false;
      })
      .addCase(createPaymentRefundThunk.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? null;
      });

    builder
      .addMatcher(
        (action) =>
          acceptPaymentRefundThunk.pending.match(action) ||
          rejectPaymentRefundThunk.pending.match(action),
        (state, action) => {
          state.resolvingByUuid[action.meta.arg.refundUuid] = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          acceptPaymentRefundThunk.fulfilled.match(action) ||
          rejectPaymentRefundThunk.fulfilled.match(action),
        (state, action) => {
          state.resolvingByUuid[action.meta.arg.refundUuid] = false;
        }
      )
      .addMatcher(
        (action) =>
          acceptPaymentRefundThunk.rejected.match(action) ||
          rejectPaymentRefundThunk.rejected.match(action),
        (state, action) => {
          state.resolvingByUuid[action.meta.arg.refundUuid] = false;
          state.error = action.payload ?? null;
        }
      );
  }
});

export const { resetPaymentRefundsState } = paymentRefundsSlice.actions;
export default paymentRefundsSlice.reducer;
